/* eslint-disable @eslint-react/dom/no-unsafe-iframe-sandbox -- Social embeds require scripts + same-origin to render */
// Social Post Embed - renders an embedded iframe for Instagram and TikTok live posts

import { ExternalLink } from "lucide-react";

interface SocialPostEmbedProps {
  /** The URL of the social post */
  postUrl: string;
}

type EmbedInfo = {
  platform: "instagram" | "tiktok";
  embedUrl: string;
};

/**
 * Parses a social post URL and returns embed info if supported.
 * Supports Instagram posts/reels and TikTok videos.
 */
export function parseEmbedUrl(postUrl: string): EmbedInfo | null {
  try {
    const url = new URL(postUrl);
    const host = url.hostname.toLowerCase();

    // Instagram: posts, reels, tv
    // e.g. https://www.instagram.com/p/ABC123/
    //      https://www.instagram.com/reel/ABC123/
    const isInstagram =
      host === "instagram.com" || host.endsWith(".instagram.com");
    if (isInstagram) {
      const match = url.pathname.match(/\/(p|reel|tv)\/([^/]+)/);
      if (match) {
        return {
          platform: "instagram",
          embedUrl: `https://www.instagram.com/${match[1]}/${match[2]}/embed/`,
        };
      }
    }

    // TikTok videos
    // e.g. https://www.tiktok.com/@user/video/1234567890
    const isTikTok = host === "tiktok.com" || host.endsWith(".tiktok.com");
    if (isTikTok) {
      const match = url.pathname.match(/\/video\/(\d+)/);
      if (match) {
        return {
          platform: "tiktok",
          embedUrl: `https://www.tiktok.com/embed/v2/${match[1]}`,
        };
      }
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Renders an embedded social post for supported platforms (Instagram, TikTok).
 * Falls back to a styled link for unsupported URLs.
 */
export function SocialPostEmbed({ postUrl }: SocialPostEmbedProps) {
  const embedInfo = parseEmbedUrl(postUrl);

  if (!embedInfo) {
    return (
      <a
        href={postUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-primary hover:underline break-all"
      >
        {postUrl}
        <ExternalLink className="h-3 w-3 shrink-0" />
      </a>
    );
  }

  const height = embedInfo.platform === "tiktok" ? 750 : 540;

  return (
    <div className="space-y-2">
      <div className="rounded-lg overflow-hidden border bg-muted max-w-md">
        <iframe
          src={embedInfo.embedUrl}
          title={`${embedInfo.platform === "instagram" ? "Instagram" : "TikTok"} post`}
          className="w-full border-0"
          style={{ height }}
          loading="lazy"
          allow="encrypted-media"
          sandbox="allow-scripts allow-same-origin allow-popups"
        />
      </div>
      <a
        href={postUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
      >
        View on {embedInfo.platform === "instagram" ? "Instagram" : "TikTok"}
        <ExternalLink className="h-3 w-3" />
      </a>
    </div>
  );
}
